import React, { useState } from 'react';
import { 
  Ticket, 
  Search, 
  CheckCircle, 
  Clock, 
  XCircle, 
  RefreshCw, 
  ShieldCheck, 
  DollarSign, 
  User, 
  Building2, 
  Calendar,
  Eye,
  Filter,
  Plus
} from 'lucide-react';
import CyberBadge from '../common/CyberBadge';
import ModalContainer from '../common/ModalContainer';
import RazorpayCheckoutButton from '../common/RazorpayCheckoutButton';

export default function BookingsManagerView({
  bookings = [],
  payments = [],
  stats = null,
  rooms = [],
  onUpdateBookingStatus,
  onCreateBooking,
}) {
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'confirmed', 'completed', 'refunded'
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // New Booking Form State
  const [newRoomId, setNewRoomId] = useState('');
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [newQuantity, setNewQuantity] = useState(1);
  const [newAmount, setNewAmount] = useState(499);
  const [newBookingStatus, setNewBookingStatus] = useState('confirmed');
  const [newPaymentStatus, setNewPaymentStatus] = useState('paid');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Real database bookings
  const displayBookings = bookings;

  const filteredBookings = displayBookings.filter((b) => {
    const userName = b.user?.name || '';
    const userEmail = b.user?.email || '';
    const roomTitle = b.room?.title || '';
    const bookingId = b._id || b.id || '';

    const matchesSearch = !searchTerm.trim() ||
      userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      roomTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookingId.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (activeTab === 'confirmed') return b.bookingStatus === 'confirmed';
    if (activeTab === 'completed') return b.bookingStatus === 'completed';
    if (activeTab === 'refunded') return b.bookingStatus === 'cancelled' || b.paymentStatus === 'refunded';

    return true;
  });

  const totalBookingsCount = displayBookings.length;
  const totalRevenue = displayBookings.reduce((acc, b) => acc + (parseFloat(b.totalAmount) || 0), 0);
  const confirmedCount = displayBookings.filter((b) => b.bookingStatus === 'confirmed').length;

  const handleCreateBookingSubmit = async (e) => {
    e.preventDefault();
    if (!newCustomerName.trim()) {
      alert('Please enter customer name');
      return;
    }
    setIsSubmitting(true);
    try {
      if (onCreateBooking) {
        await onCreateBooking({
          roomId: newRoomId || null,
          customerName: newCustomerName.trim(),
          customerEmail: newCustomerEmail.trim() || 'guest@loka.com',
          quantity: parseInt(newQuantity, 10) || 1,
          amount: parseFloat(newAmount) || 0,
          totalAmount: parseFloat(newAmount) || 0,
          bookingStatus: newBookingStatus,
          paymentStatus: newPaymentStatus,
          bookingType: 'party_ticket'
        });
      }
      setShowCreateModal(false);
      setNewCustomerName('');
      setNewCustomerEmail('');
      setNewQuantity(1);
      setNewAmount(499);
    } catch (err) {
      alert(err.message || 'Failed to create booking pass');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 className="page-title" style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Ticket size={28} style={{ color: 'var(--neon-green)' }} /> Party Ticket & Booking Escrow Manager
          </h1>
          <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Manage user ticket reservations, party bookings, escrow releases, and status overrides.
          </p>
        </div>

        <button
          className="btn btn-purple"
          style={{ padding: '12px 20px', borderRadius: '12px', fontSize: '0.88rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 0 20px rgba(139, 92, 246, 0.3)' }}
          onClick={() => setShowCreateModal(true)}
        >
          <Plus size={18} /> Upload / Issue Booking Pass
        </button>
      </div>

      {/* Analytics Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
        <div className="cyber-card" style={{ padding: '20px', background: 'rgba(0, 255, 135, 0.1)', border: '1px solid rgba(0, 255, 135, 0.3)' }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--neon-green)', fontWeight: 800, textTransform: 'uppercase' }}>Total Tickets & Bookings</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#FFFFFF', marginTop: '6px' }}>{totalBookingsCount}</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '4px' }}>Active platform reservations</div>
        </div>

        <div className="cyber-card" style={{ padding: '20px', background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
          <div style={{ fontSize: '0.78rem', color: '#A78BFA', fontWeight: 800, textTransform: 'uppercase' }}>Confirmed Pass Registrations</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#A78BFA', marginTop: '6px' }}>{confirmedCount}</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '4px' }}>Verified & Escrow Protected</div>
        </div>

        <div className="cyber-card" style={{ padding: '20px', background: 'rgba(0, 240, 255, 0.1)', border: '1px solid rgba(0, 240, 255, 0.3)' }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--neon-cyan)', fontWeight: 800, textTransform: 'uppercase' }}>Total Ticket Gross</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--neon-cyan)', marginTop: '6px' }}>₹{totalRevenue.toLocaleString()}</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '4px' }}>Escrow pool value</div>
        </div>
      </div>

      {/* Filter Tabs & Search */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', background: 'rgba(10,12,20,0.8)', padding: '12px 18px', borderRadius: '14px', border: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { id: 'all', label: `All Bookings (${displayBookings.length})` },
            { id: 'confirmed', label: `Confirmed (${confirmedCount})`, color: 'var(--neon-green)' },
            { id: 'completed', label: 'Completed' },
            { id: 'refunded', label: 'Cancelled / Refunded' },
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '0.82rem',
                  fontWeight: isActive ? 800 : 600,
                  background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                  border: isActive ? `1.5px solid ${tab.color || 'var(--neon-green)'}` : '1px solid transparent',
                  color: isActive ? '#FFFFFF' : 'var(--text-muted)',
                  cursor: 'pointer',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div style={{ position: 'relative', width: '280px' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search by ticket ID, user, or party..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px 8px 36px',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid var(--border-color)',
              color: '#FFFFFF',
              fontSize: '0.82rem',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* Bookings Table */}
      <div className="cyber-table-container" style={{ background: 'rgba(10,12,20,0.8)', borderRadius: '16px', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.88rem' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              <th style={{ padding: '16px' }}>Ticket ID</th>
              <th style={{ padding: '16px' }}>Customer Details</th>
              <th style={{ padding: '16px' }}>Party / Event</th>
              <th style={{ padding: '16px' }}>Amount</th>
              <th style={{ padding: '16px' }}>Payment Status</th>
              <th style={{ padding: '16px' }}>Booking Status</th>
              <th style={{ padding: '16px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                  No bookings found matching criteria. Click "Upload & Issue Pass" to create a new booking reservation.
                </td>
              </tr>
            ) : (
              filteredBookings.map((b) => (
                <tr key={b._id || b.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <td style={{ padding: '16px', fontWeight: 900, color: '#FFFFFF', fontFamily: 'var(--font-mono)' }}>
                    #{(b._id || b.id || '').slice(-6).toUpperCase()}
                  </td>
                <td style={{ padding: '16px' }}>
                  <div style={{ fontWeight: 800, color: '#FFFFFF' }}>{b.user?.name || 'User Customer'}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{b.user?.email}</div>
                </td>
                <td style={{ padding: '16px' }}>
                  <div style={{ fontWeight: 800, color: '#A78BFA' }}>{b.room?.title || 'Warm Home Party'}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Qty: {b.quantity || 1} Pass</div>
                </td>
                <td style={{ padding: '16px', fontWeight: 900, color: 'var(--neon-green)', fontFamily: 'var(--font-mono)' }}>
                  ₹{b.totalAmount || b.amount || 0}
                </td>
                <td style={{ padding: '16px' }}>
                  <span className={`badge ${b.paymentStatus === 'paid' ? 'badge-green' : 'badge-purple'}`}>
                    {(b.paymentStatus || 'paid').toUpperCase()}
                  </span>
                </td>
                <td style={{ padding: '16px' }}>
                  <CyberBadge status={b.bookingStatus || 'confirmed'} />
                </td>
                <td style={{ padding: '16px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
                    {b.paymentStatus !== 'paid' && (
                      <RazorpayCheckoutButton
                        amount={b.totalAmount || b.amount || 100}
                        itemName={b.room?.title || 'Party Booking Pass'}
                        bookingId={b._id || b.id}
                        customerName={b.user?.name || 'Customer'}
                        customerEmail={b.user?.email || 'customer@rapmap.com'}
                        buttonText="Pay with Razorpay"
                        onSuccess={() => {
                          if (onUpdateBookingStatus) {
                            onUpdateBookingStatus(b._id || b.id, 'confirmed', 'paid');
                          }
                        }}
                      />
                    )}

                    {b.bookingStatus !== 'completed' && onUpdateBookingStatus && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '0.75rem' }}
                        onClick={() => onUpdateBookingStatus(b._id || b.id, 'completed', 'paid')}
                      >
                        Complete
                      </button>
                    )}

                    {b.bookingStatus !== 'cancelled' && onUpdateBookingStatus && (
                      <button
                        className="btn btn-danger"
                        style={{ padding: '6px 12px', fontSize: '0.75rem' }}
                        onClick={() => onUpdateBookingStatus(b._id || b.id, 'cancelled', 'refunded')}
                      >
                        Cancel & Refund
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
        </table>
      </div>

      {/* Upload / Issue Booking Pass Modal */}
      <ModalContainer
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Upload & Issue Party Pass / Booking"
      >
        <form onSubmit={handleCreateBookingSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="form-group">
            <label style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Select Event / Party Room</label>
            <select
              className="form-control"
              value={newRoomId}
              onChange={(e) => {
                setNewRoomId(e.target.value);
                const selected = rooms.find(r => (r._id || r.id) === e.target.value);
                if (selected && selected.price) setNewAmount(selected.price);
              }}
              style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', color: '#FFFFFF', marginTop: '6px' }}
            >
              <option value="">General Platform VIP Warm Home Pass</option>
              {rooms.map(r => (
                <option key={r._id || r.id} value={r._id || r.id}>
                  {r.title} ({r.city || 'Indore'} - ₹{r.price || 0})
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Customer Full Name *</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Rahul Sharma"
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
                required
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', color: '#FFFFFF', marginTop: '6px' }}
              />
            </div>

            <div className="form-group">
              <label style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Customer Email</label>
              <input
                type="email"
                className="form-control"
                placeholder="e.g. rahul@gmail.com"
                value={newCustomerEmail}
                onChange={(e) => setNewCustomerEmail(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', color: '#FFFFFF', marginTop: '6px' }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Quantity (Passes)</label>
              <input
                type="number"
                min="1"
                max="20"
                className="form-control"
                value={newQuantity}
                onChange={(e) => setNewQuantity(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', color: '#FFFFFF', marginTop: '6px' }}
              />
            </div>

            <div className="form-group">
              <label style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Total Amount (₹)</label>
              <input
                type="number"
                min="0"
                className="form-control"
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', color: '#FFFFFF', marginTop: '6px' }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Booking Status</label>
              <select
                className="form-control"
                value={newBookingStatus}
                onChange={(e) => setNewBookingStatus(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', color: '#FFFFFF', marginTop: '6px' }}
              >
                <option value="confirmed">Confirmed (Active Pass)</option>
                <option value="completed">Completed (Attended)</option>
                <option value="pending">Pending Escrow</option>
              </select>
            </div>

            <div className="form-group">
              <label style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Payment Status</label>
              <select
                className="form-control"
                value={newPaymentStatus}
                onChange={(e) => setNewPaymentStatus(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', color: '#FFFFFF', marginTop: '6px' }}
              >
                <option value="paid">PAID (Verified Escrow)</option>
                <option value="pending">Payment Pending</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setShowCreateModal(false)}
              style={{ flex: 1, padding: '12px', borderRadius: '8px' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-purple"
              disabled={isSubmitting}
              style={{ flex: 1, padding: '12px', borderRadius: '8px', fontWeight: 800 }}
            >
              {isSubmitting ? 'Issuing Pass...' : 'Issue & Confirm Pass'}
            </button>
          </div>
        </form>
      </ModalContainer>
    </div>
  );
}
